import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2,
  Users,
  FileText,
  DollarSign,
  LogOut,
  User,
  CheckCircle2,
  XCircle,
  Clock,
  Home,
  Settings,
  Tag,
  AlertCircle,
  Loader2,
  CreditCard,
  ArrowUpRight,
  Wallet,
  TrendingUp,
  Download,
  Flag,
} from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import ServiceImagesManager from "@/components/ServiceImagesManager";
import { useTranslation } from "react-i18next";
import { formatDate } from "@/utils/dateFormat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [adminDisputes, setAdminDisputes] = useState([]);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const fetchData = async () => {
    try {
      const [statsRes, businessesRes, usersRes, projectsRes, categoriesRes, txnRes, payoutsRes, verRes, disputesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/businesses`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/projects`),
        axios.get(`${API}/categories?lang=${i18n.language?.startsWith('es') ? 'es' : 'en'}`),
        axios.get(`${API}/admin/transactions`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/payouts`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/verifications`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/disputes`).catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setBusinesses(businessesRes.data);
      setUsers(usersRes.data);
      setProjects(projectsRes.data);
      setCategories(categoriesRes.data);
      setTransactions(txnRes.data);
      setPayouts(payoutsRes.data);
      setVerifications(verRes.data);
      setAdminDisputes(disputesRes.data);
    } catch (error) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleApproveBusiness = async (businessId) => {
    setActionLoading(businessId);
    try {
      await axios.put(`${API}/admin/businesses/${businessId}/approve`);
      toast.success("Business approved successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to approve business");
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectBusiness = async (businessId) => {
    setActionLoading(businessId);
    try {
      await axios.put(`${API}/admin/businesses/${businessId}/reject`);
      toast.success("Business rejected");
      fetchData();
    } catch (error) {
      toast.error("Failed to reject business");
    } finally {
      setActionLoading(null);
    }
  };

  const handleVerificationReview = async (userId, status, reason = "") => {
    setActionLoading(`ver-${userId}`);
    try {
      await axios.put(`${API}/admin/verifications/${userId}/review`, { status, rejection_reason: reason });
      toast.success(`Verification ${status}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to review verification");
    } finally {
      setActionLoading(null);
    }
  };

  const handleResolveDispute = async (disputeId, resolution) => {
    const reason = prompt("Enter resolution reason (required):");
    if (!reason) return;
    setActionLoading(`dis-${disputeId}`);
    try {
      await axios.put(`${API}/admin/disputes/${disputeId}/resolve`, { resolution, resolution_reason: reason });
      toast.success("Dispute resolved");
      setSelectedDispute(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resolve dispute");
    } finally {
      setActionLoading(null);
    }
  };

  const exportCSV = (data, columns, filename) => {
    const header = columns.map(c => c.label).join(",");
    const rows = data.map(row => columns.map(c => {
      let val = c.getter(row);
      if (val === undefined || val === null) val = "";
      val = String(val).replace(/"/g, '""');
      return `"${val}"`;
    }).join(","));
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportTransactions = () => {
    const today = new Date().toISOString().split("T")[0];
    exportCSV(transactions, [
      { label: "Date", getter: r => formatDate(r.created_at) },
      { label: "Project", getter: r => r.project_title },
      { label: "Milestone", getter: r => r.milestone_title },
      { label: "Customer", getter: r => r.customer_name },
      { label: "Customer Email", getter: r => r.customer_email },
      { label: "Contractor", getter: r => r.business_name },
      { label: "Amount", getter: r => r.amount?.toFixed(2) },
      { label: "Platform Fee", getter: r => (r.platform_fee || 0).toFixed(2) },
      { label: "Contractor Amount", getter: r => (r.contractor_amount || 0).toFixed(2) },
      { label: "Status", getter: r => r.payment_status },
      { label: "Session ID", getter: r => r.session_id },
    ], `payments_${today}.csv`);
    toast.success("Transactions exported");
  };

  const exportPayouts = () => {
    const today = new Date().toISOString().split("T")[0];
    exportCSV(payouts, [
      { label: "Date", getter: r => formatDate(r.created_at) },
      { label: "Contractor", getter: r => r.business_name },
      { label: "Contractor Email", getter: r => r.business_email },
      { label: "Project", getter: r => r.project_title },
      { label: "Milestone", getter: r => r.milestone_title },
      { label: "Total Amount", getter: r => r.amount?.toFixed(2) },
      { label: "Platform Fee", getter: r => r.platform_fee?.toFixed(2) },
      { label: "Paid Out", getter: r => r.contractor_amount?.toFixed(2) },
      { label: "Stripe Account", getter: r => r.stripe_account_id },
      { label: "Transfer ID", getter: r => r.transfer_id },
      { label: "Status", getter: r => r.status },
    ], `payouts_${today}.csv`);
    toast.success("Payouts exported");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  const pendingBusinesses = businesses.filter((b) => b.approval_status === "pending");

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Sidebar */}
      <aside className="dashboard-sidebar hidden md:block">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-lg bg-[#D4AF37] flex items-center justify-center">
              <Building2 className="w-6 h-6 text-[#0B1F3B]" />
            </div>
            <span className="text-xl font-extrabold text-white" style={{ fontFamily: 'Manrope' }}>
              MyProjectBidder
            </span>
          </div>

          <nav className="space-y-2">
            <Link
              to="/admin"
              className="sidebar-link active flex items-center gap-3 px-4 py-3 rounded-lg text-white"
            >
              <Home className="w-5 h-5" />
              Dashboard
            </Link>
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-white/10">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                  <User className="w-5 h-5 text-[#D4AF37]" />
                </div>
              )}
              <div>
                <p className="text-white font-medium text-sm">{user?.name}</p>
                <p className="text-slate-400 text-xs">Administrator</p>
              </div>
            </div>
            <NotificationBell />
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-white/10"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {t("admin.signOut")}
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden bg-[#0B1F3B] text-white p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#D4AF37] flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#0B1F3B]" />
            </div>
            <span className="font-bold">MyProjectBidder Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-white hover:bg-white/10"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="dashboard-content p-6 md:p-8" data-testid="admin-dashboard">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-extrabold text-[#0B1F3B]" style={{ fontFamily: 'Manrope' }}>
            {t("admin.dashboard")}
          </h1>
          <p className="text-slate-600 mt-1">
            {t("admin.manageSubtitle")}
          </p>
        </div>

        {/* Pending Approvals Alert */}
        {pendingBusinesses.length > 0 && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div>
              <p className="font-semibold text-yellow-800">
                {pendingBusinesses.length} Business{pendingBusinesses.length > 1 ? "es" : ""} Pending Approval
              </p>
              <p className="text-sm text-yellow-700">
                Review and approve business accounts to let them start receiving project requests.
              </p>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="stats-card">
            <div className="flex items-center gap-3">
              <div className="icon-wrapper bg-blue-100">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#0B1F3B]">{stats?.total_users || 0}</p>
                <p className="text-sm text-slate-500">{t("admin.totalUsers")}</p>
              </div>
            </div>
          </div>
          <div className="stats-card">
            <div className="flex items-center gap-3">
              <div className="icon-wrapper bg-purple-100">
                <Building2 className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#0B1F3B]">{stats?.total_businesses || 0}</p>
                <p className="text-sm text-slate-500">{t("admin.totalBusinesses")}</p>
              </div>
            </div>
          </div>
          <div className="stats-card">
            <div className="flex items-center gap-3">
              <div className="icon-wrapper bg-green-100">
                <FileText className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#0B1F3B]">{stats?.total_projects || 0}</p>
                <p className="text-sm text-slate-500">{t("admin.totalProjects")}</p>
              </div>
            </div>
          </div>
          <div className="stats-card">
            <div className="flex items-center gap-3">
              <div className="icon-wrapper bg-yellow-100">
                <DollarSign className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#0B1F3B]">{stats?.total_estimates || 0}</p>
                <p className="text-sm text-slate-500">Estimates</p>
              </div>
            </div>
          </div>
        </div>

        {/* Revenue Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-testid="revenue-stats">
          <div className="stats-card bg-[#0B1F3B]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">${(stats?.total_revenue || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                <p className="text-sm text-slate-400">Total Revenue</p>
              </div>
            </div>
          </div>
          <div className="stats-card bg-emerald-50 border border-emerald-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-700">${(stats?.platform_fees_earned || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                <p className="text-sm text-emerald-600">Platform Fees (10%)</p>
              </div>
            </div>
          </div>
          <div className="stats-card bg-blue-50 border border-blue-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <ArrowUpRight className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-700">${(stats?.contractor_payouts_total || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                <p className="text-sm text-blue-600">Contractor Payouts</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="approvals" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1">
            <TabsTrigger value="approvals" className="data-[state=active]:bg-[#0B1F3B] data-[state=active]:text-white">
              {t("admin.pending")} ({pendingBusinesses.length})
            </TabsTrigger>
            <TabsTrigger value="businesses" className="data-[state=active]:bg-[#0B1F3B] data-[state=active]:text-white">
              {t("admin.businesses")}
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-[#0B1F3B] data-[state=active]:text-white">
              {t("admin.usersTab")}
            </TabsTrigger>
            <TabsTrigger value="categories" className="data-[state=active]:bg-[#0B1F3B] data-[state=active]:text-white">
              {t("admin.categories")}
            </TabsTrigger>
            <TabsTrigger value="payments" className="data-[state=active]:bg-[#0B1F3B] data-[state=active]:text-white" data-testid="payments-tab">
              {t("admin.paymentsTab")}
            </TabsTrigger>
            <TabsTrigger value="verifications" className="data-[state=active]:bg-[#0B1F3B] data-[state=active]:text-white" data-testid="verifications-tab">
              {t("admin.verificationsTab")} ({verifications.filter(v => v.verification_status === "submitted").length})
            </TabsTrigger>
            <TabsTrigger value="disputes" className="data-[state=active]:bg-[#0B1F3B] data-[state=active]:text-white" data-testid="disputes-tab">
              {t("admin.disputesTab")} ({adminDisputes.filter(d => ["open", "under_review"].includes(d.status)).length})
            </TabsTrigger>
            <TabsTrigger value="service-images" className="data-[state=active]:bg-[#0B1F3B] data-[state=active]:text-white" data-testid="service-images-tab">
              Service Images
            </TabsTrigger>
          </TabsList>

          {/* Pending Approvals */}
          <TabsContent value="approvals">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-6 border-b border-slate-100">
                <h2 className="text-lg font-bold text-[#0B1F3B]">Pending Business Approvals</h2>
              </div>
              {pendingBusinesses.length === 0 ? (
                <div className="empty-state py-16">
                  <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-green-300" />
                  <h3 className="text-lg font-semibold text-[#0B1F3B] mb-2">All caught up!</h3>
                  <p className="text-slate-500">No pending business approvals.</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {pendingBusinesses.map((business) => (
                    <div key={business.id} className="p-6" data-testid={`pending-business-${business.id}`}>
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                          <h3 className="font-semibold text-[#0B1F3B] mb-1">{business.business_name}</h3>
                          <p className="text-sm text-slate-600 mb-2">{business.description}</p>
                          <div className="flex flex-wrap gap-2 mb-2">
                            {business.service_categories?.slice(0, 3).map((catId) => {
                              const cat = categories.find((c) => c.id === catId);
                              return cat ? (
                                <Badge key={catId} variant="outline" className="text-xs">
                                  {cat.name}
                                </Badge>
                              ) : null;
                            })}
                          </div>
                          <p className="text-sm text-slate-500">
                            {business.city}, {business.zip_code} • Service radius: {business.service_radius} miles
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleApproveBusiness(business.id)}
                            disabled={actionLoading === business.id}
                            className="bg-green-600 hover:bg-green-700 text-white"
                            data-testid={`approve-btn-${business.id}`}
                          >
                            {actionLoading === business.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <CheckCircle2 className="w-4 h-4 mr-1" />
                                Approve
                              </>
                            )}
                          </Button>
                          <Button
                            onClick={() => handleRejectBusiness(business.id)}
                            disabled={actionLoading === business.id}
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50"
                            data-testid={`reject-btn-${business.id}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* All Businesses */}
          <TabsContent value="businesses">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-6 border-b border-slate-100">
                <h2 className="text-lg font-bold text-[#0B1F3B]">All Businesses</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Business Name</th>
                      <th>Location</th>
                      <th>Status</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {businesses.map((business) => (
                      <tr key={business.id}>
                        <td>
                          <p className="font-medium text-[#0B1F3B]">{business.business_name}</p>
                          <p className="text-sm text-slate-500">{business.phone}</p>
                        </td>
                        <td>
                          {business.city}, {business.zip_code}
                        </td>
                        <td>
                          <Badge
                            className={`${
                              business.approval_status === "approved"
                                ? "badge-approved"
                                : business.approval_status === "rejected"
                                ? "badge-rejected"
                                : "badge-pending"
                            }`}
                          >
                            {business.approval_status}
                          </Badge>
                        </td>
                        <td>{formatDate(business.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Users */}
          <TabsContent value="users">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-6 border-b border-slate-100">
                <h2 className="text-lg font-bold text-[#0B1F3B]">All Users</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id}>
                        <td className="font-medium text-[#0B1F3B]">{u.name}</td>
                        <td className="text-slate-600">{u.email}</td>
                        <td>
                          <Badge variant="outline" className="capitalize">
                            {u.user_type}
                          </Badge>
                        </td>
                        <td>
                          <Badge className={u.is_active ? "badge-approved" : "badge-rejected"}>
                            {u.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </td>
                        <td>{formatDate(u.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Categories */}
          <TabsContent value="categories">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-6 border-b border-slate-100">
                <h2 className="text-lg font-bold text-[#0B1F3B]">Service Categories</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
                {categories.map((category) => (
                  <div
                    key={category.id}
                    className="p-4 border border-slate-200 rounded-lg"
                    data-testid={`category-${category.id}`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                        <Tag className="w-5 h-5 text-[#D4AF37]" />
                      </div>
                      <h3 className="font-semibold text-[#0B1F3B]">{category.name}</h3>
                    </div>
                    <p className="text-sm text-slate-600">{category.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Payments & Payouts */}
          <TabsContent value="payments">
            <div className="space-y-6" data-testid="payments-content">
              {/* Transactions Table */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-[#0B1F3B]">Transactions</h2>
                    <p className="text-sm text-slate-500">{transactions.length} total</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {transactions.length > 0 && (
                      <Button variant="outline" size="sm" onClick={exportTransactions} className="text-xs" data-testid="export-transactions-btn">
                        <Download className="w-3 h-3 mr-1" /> Export CSV
                      </Button>
                    )}
                    <Badge className="bg-[#0B1F3B] text-white">{transactions.filter(t => t.payment_status === "paid").length} completed</Badge>
                  </div>
                </div>
                {transactions.length === 0 ? (
                  <div className="p-12 text-center">
                    <CreditCard className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p className="text-slate-500">No transactions yet.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                        <tr>
                          <th className="px-4 py-3 text-left">Date</th>
                          <th className="px-4 py-3 text-left">Project</th>
                          <th className="px-4 py-3 text-left">Milestone</th>
                          <th className="px-4 py-3 text-left">Customer</th>
                          <th className="px-4 py-3 text-left">Contractor</th>
                          <th className="px-4 py-3 text-right">Amount</th>
                          <th className="px-4 py-3 text-right">Fee</th>
                          <th className="px-4 py-3 text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {transactions.map((txn) => (
                          <tr key={txn.id} className="hover:bg-slate-50" data-testid={`txn-row-${txn.id}`}>
                            <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{formatDate(txn.created_at)}</td>
                            <td className="px-4 py-3 font-medium text-[#0B1F3B] max-w-[160px] truncate">{txn.project_title}</td>
                            <td className="px-4 py-3 text-slate-600">{txn.milestone_title || "—"}</td>
                            <td className="px-4 py-3 text-slate-600">{txn.customer_name}</td>
                            <td className="px-4 py-3 text-slate-600">{txn.business_name}</td>
                            <td className="px-4 py-3 text-right font-semibold text-[#0B1F3B]">${txn.amount?.toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
                            <td className="px-4 py-3 text-right text-emerald-600 font-medium">${(txn.platform_fee || 0).toFixed(2)}</td>
                            <td className="px-4 py-3 text-center">
                              <Badge className={`text-xs ${
                                txn.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" :
                                txn.payment_status === "expired" ? "bg-red-100 text-red-700" :
                                "bg-amber-100 text-amber-700"
                              }`}>
                                {txn.payment_status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Payouts Table */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-[#0B1F3B]">Contractor Payouts</h2>
                    <p className="text-sm text-slate-500">Funds released to contractors</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {payouts.length > 0 && (
                      <Button variant="outline" size="sm" onClick={exportPayouts} className="text-xs" data-testid="export-payouts-btn">
                        <Download className="w-3 h-3 mr-1" /> Export CSV
                      </Button>
                    )}
                    <Badge className="bg-emerald-100 text-emerald-700">{payouts.length} payouts</Badge>
                  </div>
                </div>
                {payouts.length === 0 ? (
                  <div className="p-12 text-center">
                    <Wallet className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p className="text-slate-500">No payouts yet. Payouts appear when homeowners release milestone funds.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                        <tr>
                          <th className="px-4 py-3 text-left">Date</th>
                          <th className="px-4 py-3 text-left">Contractor</th>
                          <th className="px-4 py-3 text-left">Project</th>
                          <th className="px-4 py-3 text-left">Milestone</th>
                          <th className="px-4 py-3 text-right">Total</th>
                          <th className="px-4 py-3 text-right">Fee</th>
                          <th className="px-4 py-3 text-right">Paid Out</th>
                          <th className="px-4 py-3 text-left">Stripe Account</th>
                          <th className="px-4 py-3 text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {payouts.map((p) => (
                          <tr key={p.id} className="hover:bg-slate-50" data-testid={`payout-row-${p.id}`}>
                            <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{formatDate(p.created_at)}</td>
                            <td className="px-4 py-3 font-medium text-[#0B1F3B]">{p.business_name}</td>
                            <td className="px-4 py-3 text-slate-600 max-w-[160px] truncate">{p.project_title}</td>
                            <td className="px-4 py-3 text-slate-600">{p.milestone_title || "—"}</td>
                            <td className="px-4 py-3 text-right text-[#0B1F3B]">${p.amount?.toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
                            <td className="px-4 py-3 text-right text-emerald-600">${p.platform_fee?.toFixed(2)}</td>
                            <td className="px-4 py-3 text-right font-semibold text-[#0B1F3B]">${p.contractor_amount?.toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
                            <td className="px-4 py-3 text-xs text-slate-500 font-mono">{p.stripe_account_id?.substring(0, 15)}...</td>
                            <td className="px-4 py-3 text-center">
                              <Badge className={`text-xs ${
                                p.status === "transferred" ? "bg-emerald-100 text-emerald-700" :
                                p.status === "failed" ? "bg-red-100 text-red-700" :
                                "bg-amber-100 text-amber-700"
                              }`}>
                                {p.status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Verifications */}
          <TabsContent value="verifications">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="verifications-content">
              <div className="p-6 border-b border-slate-100">
                <h2 className="text-lg font-bold text-[#0B1F3B]">Contractor Verifications</h2>
              </div>
              {verifications.length === 0 ? (
                <div className="p-12 text-center">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-500">No verification submissions yet.</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {verifications.map((ver) => {
                    const usr = users.find(u => u.id === ver.user_id);
                    return (
                      <div key={ver.id} className="p-6" data-testid={`ver-row-${ver.user_id}`}>
                        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <p className="font-semibold text-[#0B1F3B]">{usr?.name || ver.user_id}</p>
                              <Badge className={`text-xs ${
                                ver.verification_status === "approved" ? "bg-emerald-100 text-emerald-700" :
                                ver.verification_status === "rejected" ? "bg-red-100 text-red-700" :
                                ver.verification_status === "submitted" ? "bg-blue-100 text-blue-700" :
                                "bg-slate-100 text-slate-600"
                              }`}>
                                {ver.verification_status}
                              </Badge>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                              <div>
                                <span className="text-slate-500">License: </span>
                                <span className="text-[#0B1F3B] font-medium">{ver.license_number || "—"}</span>
                              </div>
                              <div>
                                <span className="text-slate-500">EIN: </span>
                                <span className="text-[#0B1F3B] font-medium">{ver.ein || "—"}</span>
                              </div>
                              <div>
                                <span className="text-slate-500">License File: </span>
                                {ver.license_file_url ? (
                                  <a href={ver.license_file_url} target="_blank" rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline font-medium" data-testid={`license-link-${ver.user_id}`}>
                                    View Document
                                  </a>
                                ) : <span className="text-slate-400">Not uploaded</span>}
                              </div>
                              <div>
                                <span className="text-slate-500">Insurance COI: </span>
                                {ver.insurance_coi_url ? (
                                  <a href={ver.insurance_coi_url} target="_blank" rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline font-medium" data-testid={`insurance-link-${ver.user_id}`}>
                                    View Document
                                  </a>
                                ) : <span className="text-slate-400">Not uploaded</span>}
                              </div>
                            </div>
                            {ver.rejection_reason && (
                              <p className="text-sm text-red-600 mt-2">Rejection reason: {ver.rejection_reason}</p>
                            )}
                          </div>
                          {ver.verification_status === "submitted" && (
                            <div className="flex gap-2 flex-shrink-0">
                              <Button
                                onClick={() => handleVerificationReview(ver.user_id, "approved")}
                                disabled={actionLoading === `ver-${ver.user_id}`}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                data-testid={`approve-ver-${ver.user_id}`}
                              >
                                {actionLoading === `ver-${ver.user_id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                                Approve
                              </Button>
                              <Button
                                onClick={() => {
                                  const reason = prompt("Enter rejection reason:");
                                  if (reason) handleVerificationReview(ver.user_id, "rejected", reason);
                                }}
                                variant="outline"
                                className="border-red-300 text-red-600 hover:bg-red-50"
                                data-testid={`reject-ver-${ver.user_id}`}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Reject
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Disputes */}
          <TabsContent value="disputes">
            <div className="space-y-4" data-testid="disputes-content">
              {/* Dispute Detail Panel */}
              {selectedDispute && (
                <div className="bg-white rounded-xl border-2 border-red-200 p-6" data-testid="dispute-detail">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-[#0B1F3B]">Dispute Details</h3>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedDispute(null)}>Close</Button>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                    <div><span className="text-slate-500">Project:</span> <span className="font-medium">{selectedDispute.project_title}</span></div>
                    <div><span className="text-slate-500">Milestone:</span> <span className="font-medium">{selectedDispute.milestone_title}</span></div>
                    <div><span className="text-slate-500">Opened by:</span> <span className="font-medium">{selectedDispute.raised_by_name} ({selectedDispute.raised_by_type})</span></div>
                    <div><span className="text-slate-500">Amount at stake:</span> <span className="font-bold text-red-600">${selectedDispute.amount_at_stake?.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span></div>
                    <div><span className="text-slate-500">Reason:</span> <span className="font-medium capitalize">{selectedDispute.reason}</span></div>
                    <div><span className="text-slate-500">Status:</span> <Badge className="bg-red-100 text-red-700">{selectedDispute.status}</Badge></div>
                  </div>
                  {selectedDispute.description && (
                    <div className="mb-4 p-3 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-700">{selectedDispute.description}</p>
                    </div>
                  )}
                  {selectedDispute.evidence_urls?.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm font-medium text-slate-600 mb-2">Evidence:</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedDispute.evidence_urls.map((url, i) => (
                          <a key={url} href={url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline bg-blue-50 px-3 py-1 rounded">Evidence {i + 1}</a>
                        ))}
                      </div>
                    </div>
                  )}
                  {["open", "under_review"].includes(selectedDispute.status) && (
                    <div className="border-t border-slate-200 pt-4 mt-4">
                      <p className="text-sm font-bold text-[#0B1F3B] mb-3">Resolve this dispute:</p>
                      <div className="grid grid-cols-3 gap-3">
                        <Button onClick={() => handleResolveDispute(selectedDispute.id, "resolved_homeowner")}
                          disabled={!!actionLoading} className="bg-blue-600 hover:bg-blue-700 text-white text-xs" data-testid="resolve-homeowner">
                          Homeowner Wins
                        </Button>
                        <Button onClick={() => handleResolveDispute(selectedDispute.id, "resolved_contractor")}
                          disabled={!!actionLoading} className="bg-amber-600 hover:bg-amber-700 text-white text-xs" data-testid="resolve-contractor">
                          Contractor Wins
                        </Button>
                        <Button onClick={() => handleResolveDispute(selectedDispute.id, "resolved_split")}
                          disabled={!!actionLoading} className="bg-purple-600 hover:bg-purple-700 text-white text-xs" data-testid="resolve-split">
                          Split 50/50
                        </Button>
                      </div>
                    </div>
                  )}
                  {selectedDispute.resolution_reason && (
                    <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm">
                      <p className="font-medium text-emerald-800">Resolution: {selectedDispute.status.replace("resolved_", "").replace("_", " ")}</p>
                      <p className="text-emerald-700 mt-1">{selectedDispute.resolution_reason}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Disputes Table */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="p-6 border-b border-slate-100">
                  <h2 className="text-lg font-bold text-[#0B1F3B]">Disputes</h2>
                  <p className="text-sm text-slate-500">{adminDisputes.filter(d => ["open", "under_review"].includes(d.status)).length} active</p>
                </div>
                {adminDisputes.length === 0 ? (
                  <div className="p-12 text-center">
                    <Flag className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p className="text-slate-500">No disputes filed yet.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                        <tr>
                          <th className="px-4 py-3 text-left">Date</th>
                          <th className="px-4 py-3 text-left">Project</th>
                          <th className="px-4 py-3 text-left">Opened By</th>
                          <th className="px-4 py-3 text-left">Reason</th>
                          <th className="px-4 py-3 text-right">Amount</th>
                          <th className="px-4 py-3 text-center">Status</th>
                          <th className="px-4 py-3 text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {adminDisputes.map((d) => (
                          <tr key={d.id} className="hover:bg-slate-50" data-testid={`dispute-row-${d.id}`}>
                            <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{formatDate(d.created_at)}</td>
                            <td className="px-4 py-3 font-medium text-[#0B1F3B] max-w-[160px] truncate">{d.project_title}</td>
                            <td className="px-4 py-3 text-slate-600">{d.raised_by_name} <span className="text-xs text-slate-400">({d.raised_by_type})</span></td>
                            <td className="px-4 py-3 capitalize text-slate-600">{d.reason}</td>
                            <td className="px-4 py-3 text-right font-semibold text-red-600">${d.amount_at_stake?.toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
                            <td className="px-4 py-3 text-center">
                              <Badge className={`text-xs ${
                                d.status === "open" ? "bg-red-100 text-red-700"
                                : d.status === "under_review" ? "bg-amber-100 text-amber-700"
                                : d.status.startsWith("resolved") ? "bg-emerald-100 text-emerald-700"
                                : "bg-slate-100 text-slate-600"
                              }`}>{d.status.replace("_", " ")}</Badge>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <Button variant="ghost" size="sm" onClick={() => setSelectedDispute(d)} className="text-xs" data-testid={`view-dispute-${d.id}`}>
                                View
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Service Images */}
          <TabsContent value="service-images">
            <ServiceImagesManager />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
